import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import NumberInput from "@/components/base/NumberInput";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import { formatCOP, formatNumber } from "@/utils/format";
import type { ExpenseCategory, Product, Wallet } from "@/types";
import type { RestockToAdvisorInput } from "@/store/AppStore";

const WALLETS: Wallet[] = ["Efectivo", "Bancolombia", "Daviplata", "Nequi", "Caja Social"];
// Tipo de compra (no fondos): la reposición se clasifica como compra por reposición.
const PURCHASE_CATEGORIES: ExpenseCategory[] = [
  "Compra por reposición",
  "Compra por encargo",
  "Compra por aumento de stock",
  "Gasto operativo",
];

const PAYMENTS: { key: RestockToAdvisorInput["payment"]; label: string; icon: string }[] = [
  { key: "pendiente", label: "Dejar pendiente", icon: "ri-time-line" },
  { key: "caja", label: "Pagar con Caja Menor", icon: "ri-safe-2-line" },
];

interface ReponerModalProps {
  open: boolean;
  advisorId: string;
  advisorName: string;
  product: Product | null;
  suggestedQty: number;
  stockCasa: number;
  cajaBalance?: number;
  onClose: () => void;
  onSubmit: (input: RestockToAdvisorInput) => void;
}

// F6 — Reposición completa en un solo paso: si hay stock en Oficina solo se
// asigna en consignación al asesor; si no alcanza, se compra lo que falte
// (ingresándolo a stock) y se deja la mercancía en consignación al asesor.
const ReponerModal = ({
  open,
  advisorId,
  advisorName,
  product,
  suggestedQty,
  stockCasa,
  cajaBalance = 0,
  onClose,
  onSubmit,
}: ReponerModalProps) => {
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [category, setCategory] = useState<ExpenseCategory>("Compra por reposición");
  const [wallet, setWallet] = useState<Wallet>("Efectivo");
  const [payment, setPayment] = useState<RestockToAdvisorInput["payment"]>("pendiente");

  useEffect(() => {
    if (!open) return;
    setQty(Math.max(1, suggestedQty));
    setCategory("Compra por reposición");
    setWallet("Efectivo");
    setPayment("pendiente");
  }, [open, suggestedQty]);

  if (!open || !product) return null;

  const qtyNum = Math.max(1, Number(qty) || 0);
  const missing = Math.max(0, qtyNum - stockCasa);
  const buyAmount = missing * product.costBase;
  const hasStock = missing === 0;
  const cajaShort = payment === "caja" && !hasStock && buyAmount > cajaBalance;

  const submit = () => {
    onSubmit({
      advisorId,
      advisorName,
      productId: product.id,
      productName: product.name,
      qty: qtyNum,
      category,
      wallet,
      payment,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Reponer producto"
      icon="ri-refresh-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button onClick={submit} disabled={cajaShort} className={`${primaryButtonClass} ${cajaShort ? "opacity-40 cursor-not-allowed" : ""}`}>
            <i className="ri-check-double-line" /> {hasStock ? "Asignar a consignación" : "Comprar y reponer"}
          </button>
        </>
      }
    >
      <div className="rounded-lg border border-background-300/40 bg-background-200/30 p-3 mb-4">
        <p className="text-sm font-semibold text-foreground-950">{product.name}</p>
        <p className="text-[11px] text-foreground-500 mt-0.5">
          Reposición para {advisorName} · precio público {formatCOP(product.pricePublic)}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Cantidad a reponer</label>
          <NumberInput min={1} value={qty} onChange={setQty} className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>Stock en Oficina</label>
          <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 text-sm font-bold text-foreground-950">
            {formatNumber(stockCasa)} und
          </div>
        </div>
      </div>

      <div
        className={`rounded-lg border p-3 mt-4 ${
          hasStock
            ? "border-accent-500/40 bg-accent-500/10"
            : "border-primary-500/50 bg-primary-500/10"
        }`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`w-8 h-8 rounded-md flex items-center justify-center ${
              hasStock ? "bg-accent-500/20 text-accent-200" : "bg-primary-500/20 text-primary-100"
            }`}
          >
            <i className={hasStock ? "ri-checkbox-circle-line" : "ri-shopping-cart-2-line"} />
          </span>
          <p className="text-[11px] text-foreground-700">
            {hasStock
              ? "Hay stock suficiente: solo se asigna la mercancía en consignación al asesor."
              : `Faltan ${formatNumber(missing)} und: se comprarán y entrarán a Oficina antes de asignarlas.`}
          </p>
        </div>

        {!hasStock && (
          <div className="mt-3 pt-3 border-t border-background-300/30">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PAYMENTS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPayment(p.key)}
                  className={`rounded-md border px-2.5 py-2 text-left cursor-pointer transition-colors ${
                    payment === p.key
                      ? "border-accent-500/50 bg-accent-500/10"
                      : "border-background-300/50 hover:border-accent-500/30"
                  }`}
                >
                  <span className="flex items-center gap-1.5 text-[10px] font-semibold text-foreground-900">
                    <i className={`${p.icon} text-accent-200`} /> {p.label}
                  </span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <div>
                <label className={labelClass}>Categoría de la compra</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
                  className={inputClass}
                >
                  {PURCHASE_CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-background-100">
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Billetera</label>
                <select
                  value={wallet}
                  onChange={(e) => setWallet(e.target.value as Wallet)}
                  className={inputClass}
                >
                  {WALLETS.map((w) => (
                    <option key={w} value={w} className="bg-background-100">
                      {w}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-[10px] text-foreground-500 mt-2 flex items-center gap-1.5">
              <i className="ri-information-line text-accent-300" /> Se registrará una compra de{" "}
              {formatCOP(buyAmount)} en Contabilidad.
            </p>
            {cajaShort && (
              <div className="mt-2 rounded-md bg-primary-500/12 border border-primary-400/40 px-3 py-2.5 flex items-start gap-2 animate-fade-up">
                <i className="ri-alert-line text-primary-100 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-foreground-950">
                    La Caja Menor no tiene fondos suficientes
                  </p>
                  <p className="text-[10px] text-foreground-700 mt-0.5">
                    Disponible {formatCOP(cajaBalance)} · necesitas {formatCOP(buyAmount)}. Pide un
                    préstamo a Juank (entra a la Caja Menor) para poder comprar.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate("/admin/cartera");
                    }}
                    className="mt-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-exchange-dollar-line" /> Ir a pedir préstamo a Juank
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReponerModal;